import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Connects to data-controller="number-series--picker"
export default class extends Controller {
  static outlets = ["number-series--sanitizer"]

  connect() {
    // Move focus to the primary "Select" button on open so keyboard
    // users can confirm the pre-selected card with Enter immediately —
    // mirrors the Tab order a sighted user would follow manually.
    const checked = this.element.querySelector('input[name="selected_number_series"]:checked')
    if (checked) {
      const selectButton = this.element.querySelector("#select_number_series_button")
      selectButton?.focus()
    }
  }

  pickNumberSeries(_event) {
    // Only handle radio button selection, don't update form or dismiss modal
    // The form will be updated when "Select" button is clicked
  }

  selectRow(event) {
    // Find the radio button within this row and select it
    const radioButton = event.currentTarget.querySelector('input[type="radio"]')
    if (radioButton) {
      radioButton.checked = true
      // Trigger the change event to update any dependent logic
      radioButton.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // Arrow-key cycling between picker cards (UX9). Wired via
  // `data-action="keydown->number-series--picker#navigate"` on the
  // picker-list root so the listener catches keys regardless of which
  // card has focus. Enter on a focused card flips its radio + dismisses.
  navigate(event) {
    const cards = Array.from(this.element.querySelectorAll(".number-series-picker-card")).filter(
      (card) => !card.classList.contains("number-series-picker-card--suspended")
    )
    if (cards.length === 0) return

    const active = document.activeElement?.closest(".number-series-picker-card")
    const currentIndex = active ? cards.indexOf(active) : -1

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault()
        const next = cards[(currentIndex + 1 + cards.length) % cards.length]
        this.#focusAndSelectCard(next)
        break
      }
      case "ArrowUp": {
        event.preventDefault()
        const prev = cards[(currentIndex - 1 + cards.length) % cards.length]
        this.#focusAndSelectCard(prev)
        break
      }
      case "Enter": {
        if (currentIndex >= 0) {
          event.preventDefault()
          this.selectCurrentNumberSeries()
        }
        break
      }
      default:
        break
    }
  }

  // Mirrors `selectRow` (the click path) for keyboard navigation: focus the
  // card, mark its radio as checked, and dispatch a `change` event so the
  // `change->number-series--picker#pickNumberSeries` listener fires. Without
  // the dispatch, arrow-key navigation would update the visual selection
  // without notifying downstream listeners — divergent from mouse behavior.
  #focusAndSelectCard(card) {
    if (!card) return
    card.focus()
    const radio = card.querySelector('input[type="radio"]')
    if (!radio) return
    radio.checked = true
    radio.dispatchEvent(new Event("change", { bubbles: true }))
  }

  selectCurrentNumberSeries() {
    const selectedRadio = this.element.querySelector('input[name="selected_number_series"]:checked')

    if (selectedRadio) {
      const numberSeries = selectedRadio.dataset.numberSeries
      const next_document_number = selectedRadio.dataset.number

      if (this.hasNumberSeriesSanitizerOutlet) {
        this.numberSeriesSanitizerOutlet.numberSeriesIdTarget.value = numberSeries
        const numberInput = this.numberSeriesSanitizerOutlet.numberSeriesInputTarget
        const isNumberlessDraft = numberInput.dataset.numberSeriesDraftPreview === "true"

        if (isNumberlessDraft) {
          numberInput.placeholder = next_document_number
        } else {
          numberInput.value = next_document_number
        }

        this.numberSeriesSanitizerOutlet.setNumberSeriesIdAttribute(numberSeries)
        if (!isNumberlessDraft) {
          this.numberSeriesSanitizerOutlet.triggerEventOnChange()
        }
      }

      this.dismissModal()
    }
  }

  dismissModal() {
    const modal = this.element.closest(".modal")
    if (modal) {
      const modalInstance = Modal.getInstance(modal)
      if (modalInstance) {
        modalInstance.hide()
      }
    }
  }
}
